"use client";

import * as React from "react";
import { useTranslations } from "next-intl";
import {
  ArrowUpRight,
  Copy,
  FileText,
  Flag,
  Link2,
  Loader2,
  Phone,
  User,
  Users,
  Video,
} from "lucide-react";

import { cn } from "@/src/lib/utils";
import { formatDate, formatDateTime, formatTime } from "@/shared/dateFormat";
import { ScoreCell } from "@/app/assignments/[id]/components/DataTable/ScoreCell";
import { LivePulse } from "./PresenceAvatar";
import type {
  AssignmentHit,
  AttemptSummary,
  GroupHit,
  LiveState,
  LiveStatus,
  StudentHit,
  ViolationSummary,
} from "./globalSearchApi";

export type PreviewSelection =
  | { kind: "student"; student: StudentHit }
  | { kind: "participant"; student: StudentHit; assignment: AssignmentHit }
  | { kind: "assignment"; assignment: AssignmentHit }
  | { kind: "group"; group: GroupHit }
  | {
      kind: "session";
      student: StudentHit | null;
      studentId: number;
      assignmentId: number;
      assignmentName: string | null;
      attemptId: number | null;
      isRunning: boolean;
    }
  | null;

export interface PreviewData {
  attempts: AttemptSummary[];
  studentAssignments: AssignmentHit[];
  assignmentStudents: StudentHit[];
  participantsCount: number | null;
  violations: ViolationSummary[];
  violationsTotal: number;
  live: LiveStatus | null;
  presence: Record<number, LiveStatus>;
  trustScores: Record<number, number | null> | null;
  isLoading: boolean;
}

interface SearchPreviewProps {
  selection: PreviewSelection;
  data: PreviewData;
  onOpenAssignment: (assignment: AssignmentHit, student?: StudentHit) => void;
  onOpenSession: () => void;
  onCopyLink: () => void;
  onCall: (student: StudentHit) => void;
  onShowParticipants: (assignment: AssignmentHit) => void;
  onSelectStudent: (student: StudentHit) => void;
  onCopyAttempt: (attemptId: number) => void;
  onWatchLive: (assignment: AssignmentHit, student: StudentHit) => void;
}

const avatarClass = "flex shrink-0 items-center justify-center rounded-full bg-muted font-medium";

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

const minutesSince = (input: string | null): number | null => {
  if (!input) return null;

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return null;

  const distance = Date.now() - date.getTime();
  if (distance < 0) return null;

  return Math.max(1, Math.round(distance / 60_000));
};

const recentTime = (input: string | null): string | null => {
  if (!input) return null;

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return null;

  const distance = Date.now() - date.getTime();
  if (distance < 0 || distance > TWELVE_HOURS_MS) return null;

  return formatTime(date);
};

const seenAt = (input: string | null): string | null => {
  if (!input) return null;

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return null;

  const distance = Date.now() - date.getTime();
  if (distance < 0) return null;

  return distance > TWELVE_HOURS_MS ? formatDateTime(date) : formatTime(date);
};

const initials = (name: string) =>
  name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar: React.FC<{ student: StudentHit; large?: boolean }> = ({ student, large }) => {
  const [failed, setFailed] = React.useState(false);
  const size = large ? "h-14 w-14 text-base" : "h-8 w-8 text-[11px]";

  if (student.photo && !failed) {
    return (
      <img
        src={student.photo}
        alt=""
        className={cn("shrink-0 rounded-full object-cover", size)}
        onError={() => setFailed(true)}
      />
    );
  }

  return <span className={cn(avatarClass, size)}>{initials(student.name) || "?"}</span>;
};

const CHIP_TONE: Record<LiveState, "live" | "warn" | "muted"> = {
  live: "live",
  "in-exam": "warn",
  away: "muted",
  finished: "muted",
  "not-started": "muted",
  offline: "muted",
};

const CHIP_KEY: Record<LiveState, string> = {
  live: "label-global-search-chip-live",
  "in-exam": "label-global-search-chip-no-camera",
  away: "label-global-search-chip-away",
  finished: "label-global-search-chip-finished",
  "not-started": "label-global-search-chip-not-started",
  offline: "label-global-search-chip-away",
};

const PresenceChip: React.FC<{ state: LiveState; t: ReturnType<typeof useTranslations> }> = ({
  state,
  t,
}) => {
  const tone = CHIP_TONE[state];

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset",
        tone === "live"
          ? "bg-red-50 text-red-700 ring-red-200"
          : tone === "warn"
          ? "bg-amber-50 text-amber-700 ring-amber-200"
          : "bg-muted text-muted-foreground ring-transparent"
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          tone === "live" ? "bg-red-500" : tone === "warn" ? "bg-amber-500" : "bg-zinc-300"
        )}
      />
      {t(CHIP_KEY[state])}
    </span>
  );
};

const FactRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-2">
    <span className="shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <span className="line-clamp-2 min-w-0 text-right text-xs" title={value}>
      {value}
    </span>
  </div>
);

const ActionButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: "default" | "primary";
}> = ({ icon, label, onClick, tone = "default" }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "te-global-search__action flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors",
      tone === "primary"
        ? "border-transparent bg-primary text-primary-foreground hover:opacity-90"
        : "hover:bg-muted"
    )}
  >
    {icon}
    {label}
  </button>
);

export function SearchPreview({
  selection,
  data,
  onOpenAssignment,
  onOpenSession,
  onCopyLink,
  onCall,
  onShowParticipants,
  onSelectStudent,
  onCopyAttempt,
  onWatchLive,
}: SearchPreviewProps) {
  const t = useTranslations();

  const violationLabel = (type: string): string => {
    const key = `verdict-action-${type}`;
    return t.has(key) ? t(key) : type;
  };

  const assignmentMeta = (assignment: AssignmentHit) =>
    [
      assignment.status === "process"
        ? t("label-global-search-exam-window-open")
        : t("label-realtime-finished"),
      typeof assignment.studentsCount === "number"
        ? t("label-global-search-participants-count", { count: assignment.studentsCount })
        : null,
      assignment.isProctoring ? t("label-global-search-with-proctoring") : null,
    ]
      .filter(Boolean)
      .join(" · ");

  if (!selection) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-xs text-muted-foreground">
        {t("label-global-search-preview-empty")}
      </div>
    );
  }

  if (selection.kind === "participant") {
    const { student, assignment } = selection;
    const status = data.presence[assignment.id] ?? null;
    const attempts = data.attempts;
    const isLive = status?.state === "live";

    return (
      <div className="flex h-full flex-col">
        <div className="space-y-5 overflow-y-auto p-5">
          <div className="flex items-start gap-4">
            <Avatar student={student} large />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <span className="truncate text-base font-semibold leading-tight">
                  {student.name}
                </span>
                {status && <PresenceChip state={status.state} t={t} />}
              </div>
              <div className="mt-1 truncate text-xs text-muted-foreground">{assignment.name}</div>
              {student.groups.length > 0 && (
                <div className="mt-1 truncate text-xs text-muted-foreground">
                  {student.groups.join(", ")}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("label-global-search-attempts")}
              </span>
              {attempts.length > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  {t("label-attempts-count", { count: attempts.length })}
                </span>
              )}
            </div>

            {data.isLoading && attempts.length === 0 && (
              <div className="flex items-center gap-2 px-1 py-3 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("label-loading")}
              </div>
            )}

            {!data.isLoading && attempts.length === 0 && (
              <div className="rounded-lg bg-muted/60 px-3 py-2.5 text-xs text-muted-foreground">
                {t("label-global-search-no-attempts")}
              </div>
            )}

            {attempts.length > 0 && (
              <div className="divide-y overflow-hidden rounded-lg border">
                {attempts.map((attempt) => (
                  <div key={attempt.id} className="flex items-center gap-2 px-3 py-2.5">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium">
                        {t("label-session-number", { number: attempt.id })}
                      </span>
                      <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                        {[
                          t("label-attempt-number", { number: attempt.number }),
                          attempt.createdAt
                            ? formatDateTime(new Date(attempt.createdAt))
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => onCopyAttempt(attempt.id)}
                      title={t("btn-global-search-copy-session")}
                      aria-label={t("btn-global-search-copy-session")}
                      className="te-global-search__copy-attempt shrink-0 rounded-md border p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto flex items-center gap-2 border-t p-4">
          <button
            type="button"
            onClick={() => onOpenAssignment(assignment, student)}
            className="te-global-search__open-details flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent bg-primary px-2.5 py-2 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
            {t("btn-global-search-open-details")}
          </button>
          {isLive && (
            <button
              type="button"
              onClick={() => onWatchLive(assignment, student)}
              className="te-global-search__watch-live flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
            >
              <LivePulse size="sm" />
              {t("btn-global-search-watch-live")}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (selection.kind === "student" || selection.kind === "session") {
    const student = selection.student;
    const isSession = selection.kind === "session";
    const presenceOf = (assignment: AssignmentHit): LiveStatus | null =>
      data.presence[assignment.id] ?? null;

    const exams = [...data.studentAssignments].sort((a, b) => {
      const left = a.startingAt ? new Date(a.startingAt).getTime() : 0;
      const right = b.startingAt ? new Date(b.startingAt).getTime() : 0;
      return right - left;
    });

    const headline = exams
      .map((assignment) => ({ assignment, status: presenceOf(assignment) }))
      .find(({ status }) => status?.state === "live" || status?.state === "in-exam");

    const isLive = headline?.status?.state === "live";

    const lastKnown =
      Object.values(data.presence)
        .map((item) => item.lastSeen)
        .filter((value): value is string => Boolean(value))
        .sort()
        .at(-1) ?? null;

    const lastSeenText = headline ? null : seenAt(lastKnown);

    const facts = student
      ? [
          { label: t("label-email"), value: student.email },
          { label: t("label-phone"), value: student.phone },
          { label: t("label-global-search-external-id"), value: student.externalId },
          { label: t("label-global-search-groups"), value: student.groups.join(", ") || null },
          {
            label: t("label-global-search-registered"),
            value: student.registerDate ? formatDateTime(new Date(student.registerDate)) : null,
          },
          {
            label: t("label-global-search-last-activity"),
            value: student.lastActivityDate
              ? formatDateTime(new Date(student.lastActivityDate))
              : null,
          },
        ].filter((fact): fact is { label: string; value: string } => Boolean(fact.value))
      : [];

    const headlineHint = isLive
      ? recentTime(headline?.status?.cameraSince ?? null)
        ? t("label-global-search-live-since", { time: recentTime(headline?.status?.cameraSince ?? null) as string })
        : null
      : headline
      ? minutesSince(headline.status?.lastSeen ?? null) != null
        ? t("label-global-search-active-ago", {
            minutes: minutesSince(headline.status?.lastSeen ?? null) as number,
          })
        : null
      : null;

    return (
      <div className="space-y-5 p-5">
        <div className="flex items-start gap-4">
          {student ? (
            <Avatar student={student} large />
          ) : (
            <span className={cn(avatarClass, "h-14 w-14")}>
              <User className="h-6 w-6 text-muted-foreground" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <span className="truncate text-base font-semibold leading-tight">
                {student?.name ??
                  t("label-global-search-session", {
                    number: isSession ? selection.attemptId ?? "" : "",
                  })}
              </span>
              {headline?.status && <PresenceChip state={headline.status.state} t={t} />}
            </div>
            {headline && (
              <div className="mt-1 truncate text-xs text-muted-foreground">
                {[headline.assignment.name, headlineHint].filter(Boolean).join(" · ")}
              </div>
            )}
            {lastSeenText && (
              <div className="mt-1 truncate text-xs text-muted-foreground">
                {t("label-global-search-last-seen", { time: lastSeenText })}
              </div>
            )}

          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {isLive && headline && student && (
            <ActionButton
              icon={<Video className="h-3.5 w-3.5" />}
              label={t("btn-watch-online")}
              onClick={() => onOpenAssignment(headline.assignment, student)}
              tone="primary"
            />
          )}

          {student && isLive && (
            <ActionButton
              icon={<Phone className="h-3.5 w-3.5" />}
              label={t("btn-call")}
              onClick={() => onCall(student)}
            />
          )}

          <ActionButton
            icon={<Link2 className="h-3.5 w-3.5" />}
            label={t("btn-copy-ticket-link")}
            onClick={onCopyLink}
          />
        </div>

        {data.isLoading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("label-loading")}
          </div>
        )}

        {student && facts.length > 0 && (
          <div>
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("label-global-search-facts")}
            </div>
            <div className="divide-y rounded-lg border px-3">
              {facts.map((fact) => (
                <FactRow key={fact.label} label={fact.label} value={fact.value} />
              ))}
            </div>
          </div>
        )}

        {exams.length === 0 && !data.isLoading && (
          <div className="rounded-lg bg-muted/60 px-3 py-2.5 text-xs text-muted-foreground">
            {t("label-global-search-no-exams")}
          </div>
        )}

        {exams.length > 0 && (
          <div>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("label-global-search-student-assignments")}
            </div>
            <div className="divide-y overflow-hidden rounded-lg border">
              {exams.slice(0, 8).map((assignment) => {
                const status = presenceOf(assignment);

                return (
                  <button
                    key={assignment.id}
                    type="button"
                    onClick={() =>
                      isSession && assignment.id === selection.assignmentId
                        ? onOpenSession()
                        : student && onOpenAssignment(assignment, student)
                    }
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-muted"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium">{assignment.name}</span>
                      <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                        {[
                          assignment.startingAt
                            ? formatDate(new Date(assignment.startingAt))
                            : null,
                          assignment.status === "process"
                            ? t("label-global-search-window-open")
                            : t("label-global-search-window-closed"),
                          typeof assignment.studentsCount === "number"
                            ? t("label-global-search-participants-count", {
                                count: assignment.studentsCount,
                              })
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    {status && <PresenceChip state={status.state} t={t} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isSession && data.violations.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("label-global-search-violations")}
              </span>
              <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                {data.violationsTotal}
              </span>
            </div>
            <div className="space-y-1">
              {data.violations.slice(0, 6).map((violation) => (
                <div
                  key={violation.type}
                  className="flex items-center justify-between gap-3 rounded-md bg-muted/60 px-3 py-2 text-xs"
                >
                  <span className="min-w-0 truncate">{violationLabel(violation.type)}</span>
                  <span className="shrink-0 text-muted-foreground">×{violation.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (selection.kind === "assignment") {
    const assignment = selection.assignment;

    return (
      <div className="space-y-3 p-4">
        <div>
          <div className="text-base font-semibold">{assignment.name}</div>
          <div className="text-xs text-muted-foreground">{assignmentMeta(assignment)}</div>
        </div>

        {(assignment.studentsCount ?? data.participantsCount) != null && (
          <div className="rounded-lg border p-3 text-center">
            <div className="text-2xl font-semibold">
              {assignment.studentsCount ?? data.participantsCount}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {t("label-global-search-participants")}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          <ActionButton
            icon={<Users className="h-3.5 w-3.5" />}
            label={t("label-global-search-participants")}
            onClick={() => onShowParticipants(assignment)}
            tone="primary"
          />
          <ActionButton
            icon={<FileText className="h-3.5 w-3.5" />}
            label={t("label-global-search-open-exam")}
            onClick={() => onOpenAssignment(assignment)}
          />
        </div>

        {data.isLoading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("label-loading")}
          </div>
        )}

        {data.assignmentStudents.length > 0 && (
          <div>
            <div className="mb-1 flex items-center gap-2">
              <Flag className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-[11px] uppercase text-muted-foreground">
                {t("label-global-search-sort-violations")}
              </span>
            </div>
            <div className="space-y-1">
              {data.assignmentStudents.slice(0, 6).map((student) => (
                <button
                  key={student.id}
                  type="button"
                  onClick={() => onSelectStudent(student)}
                  className="flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs hover:bg-muted"
                >
                  <Avatar student={student} />
                  <span className="min-w-0 flex-1 truncate">{student.name}</span>
                  <span className="shrink-0 [&_button]:bg-transparent">
                    <ScoreCell
                      points={0}
                      credibility={data.trustScores?.[student.attemptId ?? -1] ?? student.attemptCredibility ?? -1}
                      hasTrustScore={
                        student.attemptId != null &&
                        data.trustScores?.[student.attemptId] != null
                      }
                      compact
                      hidePoints
                    />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      <div>
        <div className="text-base font-semibold">{selection.group.name}</div>
        <div className="text-xs text-muted-foreground">
          {t("label-global-search-group-students")}
        </div>
      </div>

      {data.isLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t("label-loading")}
        </div>
      )}

      <div className="space-y-1">
        {data.assignmentStudents.slice(0, 12).map((student) => (
          <button
            key={student.id}
            type="button"
            onClick={() => onSelectStudent(student)}
            className="flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs hover:bg-muted"
          >
            <Avatar student={student} />
            <span className="min-w-0 flex-1 truncate">{student.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default SearchPreview;
